import { TypedDocumentNode, gql } from '@apollo/client';
import { ReactNode } from 'react';

import { Title6 } from '@sorare/core/src/atoms/typography';
import {
  TransactionEntry,
  messages,
} from '@sorare/core/src/components/mySorare/TransactionsHistory/common/TransactionEntry';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { isType } from '@sorare/core/src/gql';

import { RequestReceipt } from '../RequestReceipt';
import { AccountEntry_authorizationRequest } from './__generated__/index.graphql';

export { messages };

type Props = {
  title: string | ReactNode;
  payment: AccountEntry_authorizationRequest;
  children: ReactNode;
};

export const AccountEntry = ({ title, payment, children }: Props) => {
  const { formatNumber } = useIntlContext();
  const { request, createdAt } = payment;
  if (
    !(
      isType(request, 'MangopayCreditCardAuthorizationRequest') ||
      isType(request, 'StripeCreditCardAuthorizationRequest') ||
      isType(request, 'CheckoutCreditCardAuthorizationRequest')
    ) ||
    !request.amount ||
    !request.currency
  )
    return null;

  return (
    <TransactionEntry
      date={createdAt}
      amount={
        <Title6 color="var(--c-white)">
          {formatNumber(-request.amount / 100, {
            style: 'currency',
            currency: request.currency,
          })}
        </Title6>
      }
      {...(isType(request, 'StripeCreditCardAuthorizationRequest')
        ? { action: <RequestReceipt receiptUrl={request.receiptUrl} /> }
        : {})}
      title={title}
    >
      {children}
    </TransactionEntry>
  );
};

AccountEntry.fragments = {
  payment: gql`
    fragment AccountEntry_authorizationRequest on AuthorizationRequest {
      id
      consumedAt
      createdAt
      request {
        ... on MangopayCreditCardAuthorizationRequest {
          id
          amount
          currency
        }
        ... on StripeCreditCardAuthorizationRequest {
          id
          amount
          currency
          receiptUrl
        }
        ... on CheckoutCreditCardAuthorizationRequest {
          id
          amount
          currency
        }
      }
    }
  ` as TypedDocumentNode<AccountEntry_authorizationRequest>,
};
